import { Component, inject, OnInit, signal } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormBuilder, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { Subject } from 'rxjs';
import { debounceTime, distinctUntilChanged, switchMap } from 'rxjs/operators';
import { OrganizationService } from '../../../api/gen/api/organization.service';
import { ParkingSpaceService } from '../../../api/gen/api/parkingSpace.service';
import { CreateOrganizationRequest } from '../../../api/gen/model/createOrganizationRequest';
import { CreatePoolParkingConfigRequest } from '../../../api/gen/model/createPoolParkingConfigRequest';
import { OrganizationLookupDto } from '../../../api/gen/model/organizationLookupDto';
import { ParkingSpaceDto } from '../../../api/gen/model/parkingSpaceDto';
import { ParkingZoneDto } from '../../../api/gen/model/parkingZoneDto';
import { PoolOverflowBehavior } from '../../../api/gen/model/poolOverflowBehavior';
import { AccessWindowFieldComponent, AccessWindowValue } from '../../shared/access-window-field/access-window-field.component';
import {
    JjamesButtonComponent, JJamesInputComponent, JJamesCardComponent,
    JJamesAlertComponent, COUNTRIES, DEFAULT_COUNTRY_CODE
} from 'shared-lib';

@Component({
    selector: 'app-org-create',
    standalone: true,
    imports: [
        CommonModule,
        ReactiveFormsModule,
        JjamesButtonComponent,
        JJamesInputComponent,
        JJamesCardComponent,
        JJamesAlertComponent,
        AccessWindowFieldComponent
    ],
    templateUrl: './org-create.component.html',
    styleUrls: ['./org-create.component.scss']
})
export class OrgCreateComponent implements OnInit {
    private readonly fb = inject(FormBuilder);
    private readonly orgService = inject(OrganizationService);
    private readonly parkingSpaceService = inject(ParkingSpaceService);
    private readonly route = inject(ActivatedRoute);
    private readonly router = inject(Router);

    /** ISO 3166-1 alpha-2 options for the billing country dropdown. */
    readonly countries = COUNTRIES;

    // Mode: either creating a new organization or linking an existing one
    mode = signal<'create' | 'link'>('create');
    selectedOrgToLink = signal<OrganizationLookupDto | null>(null);

    // Spaces & Zones for the operator
    accessibleSpaces = signal<ParkingSpaceDto[]>([]);
    selectedSpaceId = signal<string>('');
    spaceZones = signal<ParkingZoneDto[]>([]);
    loadingSpaces = signal<boolean>(false);
    loadingZones = signal<boolean>(false);

    // Autocomplete / Search lookup
    private searchSubject = new Subject<string>();
    lookupResults = signal<OrganizationLookupDto[]>([]);
    isSearching = signal<boolean>(false);
    showLookupDropdown = signal<boolean>(false);

    // Main Org Form
    orgForm: FormGroup;
    isSubmitting = false;
    errorMessage: string | null = null;
    successMessage: string | null = null;

    // Optional Initial Pool Parking Config
    includePoolConfig = signal<boolean>(false);
    poolForm: FormGroup;
    poolWindow = signal<AccessWindowValue>({ start: null, end: null, days: null });
    selectedZoneIds = signal<Set<string>>(new Set());

    readonly overflowBehaviors: ReadonlyArray<{ value: PoolOverflowBehavior; label: string; hint: string }> = [
        { value: PoolOverflowBehavior.Deny, label: 'Deny', hint: 'Pool full → refuse entry or short-term fallback.' },
        { value: PoolOverflowBehavior.OrgPays, label: 'Organization pays', hint: 'Admit stay and bill the organization.' },
        { value: PoolOverflowBehavior.ParkerPays, label: 'Parker pays', hint: 'Admit stay and parker pays at exit.' }
    ];

    constructor() {
        this.orgForm = this.fb.group({
            name: ['', [Validators.required, Validators.minLength(2), Validators.maxLength(200)]],
            description: [''],
            contactPerson: [''],
            contactPhone: [''],
            firstAddressLine: [''],
            secondAddressLine: [''],
            locality: [''],
            postCode: [''],
            region: [''],
            countryCode: [DEFAULT_COUNTRY_CODE]
        });

        const today = new Date().toISOString().substring(0, 10);
        this.poolForm = this.fb.group({
            name: ['', [Validators.required, Validators.minLength(2), Validators.maxLength(200)]],
            description: [''],
            maxCarsAllowed: [5, [Validators.required, Validators.min(1)]],
            validFrom: [today, Validators.required],
            validTo: [''],
            overflowBehavior: [PoolOverflowBehavior.Deny, Validators.required]
        });
    }

    ngOnInit(): void {
        this.loadAccessibleSpaces();
        this.setupSearchLookup();

        this.orgForm.get('name')?.valueChanges.subscribe(nameVal => {
            if (this.mode() === 'create') {
                this.searchSubject.next(nameVal || '');
                if (!this.poolForm.get('name')?.dirty) {
                    this.poolForm.patchValue({ name: nameVal ? `${nameVal} Pool` : '' });
                }
            }
        });
    }

    private setupSearchLookup(): void {
        this.searchSubject.pipe(
            debounceTime(300),
            distinctUntilChanged(),
            switchMap(term => {
                if (!term || term.trim().length < 2) {
                    this.isSearching.set(false);
                    this.lookupResults.set([]);
                    this.showLookupDropdown.set(false);
                    return [];
                }
                this.isSearching.set(true);
                return this.orgService.organizationsLookupExistingPost({
                    searchTerm: term.trim(),
                    targetParkingSpaceId: this.selectedSpaceId() || undefined,
                    maxResults: 6
                });
            })
        ).subscribe({
            next: (results) => {
                this.isSearching.set(false);
                this.lookupResults.set(results || []);
                this.showLookupDropdown.set((results || []).length > 0);
            },
            error: (err) => {
                this.isSearching.set(false);
                this.lookupResults.set([]);
                this.showLookupDropdown.set(false);
                console.error('Lookup search error:', err);
            }
        });
    }

    loadAccessibleSpaces(): void {
        this.loadingSpaces.set(true);
        this.parkingSpaceService.parkingSpaceFilterPost(
            undefined, undefined, undefined, undefined, undefined, undefined, 0, 100, 'Name'
        ).subscribe({
            next: (result) => {
                const spaces = result?.items ?? [];
                this.accessibleSpaces.set(spaces);
                this.loadingSpaces.set(false);

                // Default space from query param or first accessible space
                const querySpaceId = this.route.snapshot.queryParamMap.get('spaceId');
                const defaultSpace = (querySpaceId && spaces.some(s => s.id === querySpaceId))
                    ? querySpaceId
                    : (spaces[0]?.id ?? '');

                if (defaultSpace) {
                    this.onSpaceSelected(defaultSpace);
                }
            },
            error: (err) => {
                this.loadingSpaces.set(false);
                console.error('Error loading spaces:', err);
            }
        });
    }

    onSpaceSelected(spaceId: string): void {
        this.selectedSpaceId.set(spaceId);
        this.selectedZoneIds.set(new Set());
        if (!spaceId) {
            this.spaceZones.set([]);
            return;
        }

        this.loadingZones.set(true);
        // A space carries its own zones, and parkingZoneFilterPost only filters by parking lot,
        // never by space - so fetch the space, the way every other zone picker here does.
        this.parkingSpaceService.parkingSpaceParkingSpaceIdGet(spaceId).subscribe({
            next: (dto) => {
                const zones = dto?.parkingZones ?? [];
                this.spaceZones.set(zones);
                this.loadingZones.set(false);
                // Pre-select all zones of the space by default for convenient pool creation
                this.selectedZoneIds.set(new Set(zones.map(z => z.id!).filter(Boolean)));
            },
            error: (err) => {
                this.loadingZones.set(false);
                console.error('Error loading zones:', err);
            }
        });
    }

    selectExistingOrg(org: OrganizationLookupDto): void {
        this.selectedOrgToLink.set(org);
        this.mode.set('link');
        this.showLookupDropdown.set(false);
        this.poolForm.patchValue({ name: `${org.name} Pool` });
    }

    switchToCreateMode(): void {
        this.mode.set('create');
        this.selectedOrgToLink.set(null);
        this.showLookupDropdown.set(false);
    }

    toggleZoneSelection(zoneId: string): void {
        const next = new Set(this.selectedZoneIds());
        if (next.has(zoneId)) {
            next.delete(zoneId);
        } else {
            next.add(zoneId);
        }
        this.selectedZoneIds.set(next);
    }

    onSubmit(): void {
        if (!this.selectedSpaceId()) {
            this.errorMessage = 'Please select a parking space for this organization.';
            return;
        }

        if (this.mode() === 'link') {
            this.submitLinkExisting();
        } else {
            this.submitCreateNew();
        }
    }

    private submitLinkExisting(): void {
        const org = this.selectedOrgToLink();
        if (!org?.id || this.isSubmitting) return;

        const orgId = org.id;
        this.isSubmitting = true;
        this.errorMessage = null;
        this.successMessage = null;

        // Step 1: Link space to organization
        this.orgService.organizationsOrganizationIdParkingSpacesPost(orgId, {
            parkingSpaceId: this.selectedSpaceId()
        }).subscribe({
            next: () => {
                // Step 2: If pool config is included, create pool config
                if (this.includePoolConfig()) {
                    this.createPoolConfigForOrg(orgId);
                } else {
                    this.finishSuccess(orgId, `Linked "${org.name}" to this parking space successfully!`);
                }
            },
            error: (err) => {
                this.isSubmitting = false;
                this.errorMessage = err.error?.title || err.error?.detail || 'Failed to link organization to this space.';
            }
        });
    }

    private submitCreateNew(): void {
        if (this.orgForm.invalid || this.isSubmitting) {
            this.markFormGroupTouched(this.orgForm);
            return;
        }

        if (this.includePoolConfig() && this.poolForm.invalid) {
            this.markFormGroupTouched(this.poolForm);
            return;
        }

        this.isSubmitting = true;
        this.errorMessage = null;
        this.successMessage = null;

        const formVal = this.orgForm.value;
        const poolVal = this.poolForm.value;

        const poolConfigs: CreatePoolParkingConfigRequest[] = [];
        if (this.includePoolConfig()) {
            poolConfigs.push({
                name: poolVal.name,
                description: poolVal.description || undefined,
                maxCarsAllowed: poolVal.maxCarsAllowed,
                validFrom: new Date(poolVal.validFrom).toISOString(),
                validTo: poolVal.validTo ? new Date(poolVal.validTo).toISOString() : undefined,
                overflowBehavior: poolVal.overflowBehavior ?? PoolOverflowBehavior.Deny,
                allowedZoneIds: Array.from(this.selectedZoneIds()),
                poolAccessWindowStart: this.poolWindow().start,
                poolAccessWindowEnd: this.poolWindow().end,
                poolAccessWindowDays: this.poolWindow().days
            });
        }

        const request: CreateOrganizationRequest = {
            name: formVal.name.trim(),
            description: formVal.description || undefined,
            contactPerson: formVal.contactPerson || undefined,
            contactPhone: formVal.contactPhone || undefined,
            billingAddress: {
                firstAddressLine: formVal.firstAddressLine || undefined,
                secondAddressLine: formVal.secondAddressLine || undefined,
                locality: formVal.locality || undefined,
                postCode: formVal.postCode || undefined,
                region: formVal.region || undefined,
                countryCode: formVal.countryCode || undefined
            },
            billingConfiguration: {},
            parkingSpaceIds: [this.selectedSpaceId()],
            poolParkConfigs: poolConfigs.length > 0 ? poolConfigs : undefined
        };

        this.orgService.organizationsPost(request).subscribe({
            next: (createdOrg) => {
                this.finishSuccess(createdOrg.id!, 'Organization created and linked to space successfully!');
            },
            error: (err) => {
                this.isSubmitting = false;
                this.errorMessage = err.error?.title || err.error?.detail || 'An error occurred while creating the organization.';
            }
        });
    }

    private createPoolConfigForOrg(orgId: string): void {
        const poolVal = this.poolForm.value;
        const request: CreatePoolParkingConfigRequest = {
            name: poolVal.name,
            description: poolVal.description || undefined,
            maxCarsAllowed: poolVal.maxCarsAllowed,
            validFrom: new Date(poolVal.validFrom).toISOString(),
            validTo: poolVal.validTo ? new Date(poolVal.validTo).toISOString() : undefined,
            overflowBehavior: poolVal.overflowBehavior ?? PoolOverflowBehavior.Deny,
            allowedZoneIds: Array.from(this.selectedZoneIds()),
            poolAccessWindowStart: this.poolWindow().start,
            poolAccessWindowEnd: this.poolWindow().end,
            poolAccessWindowDays: this.poolWindow().days
        };

        this.orgService.organizationsOrganizationIdPoolParkingConfigsPost(orgId, request).subscribe({
            next: () => {
                this.finishSuccess(orgId, 'Organization linked and pool config created successfully!');
            },
            error: (err) => {
                this.isSubmitting = false;
                this.errorMessage = 'Organization linked, but pool config creation failed: ' + (err.error?.title || err.error?.detail || err.message);
            }
        });
    }

    private finishSuccess(orgId: string, message: string): void {
        this.isSubmitting = false;
        this.successMessage = message;
        setTimeout(() => {
            this.router.navigate(['/org-management', orgId]);
        }, 1200);
    }

    onCancel(): void {
        this.router.navigate(['/org-management']);
    }

    onClearError(): void {
        this.errorMessage = null;
    }

    onClearSuccess(): void {
        this.successMessage = null;
    }

    isFieldInvalid(fieldName: string): boolean {
        const field = this.orgForm.get(fieldName);
        return !!(field && field.invalid && (field.dirty || field.touched));
    }

    getFieldError(fieldName: string): string {
        const field = this.orgForm.get(fieldName);
        if (field?.errors?.['required']) return `${fieldName} is required`;
        if (field?.errors?.['minlength']) return `Minimum ${field.errors['minlength'].requiredLength} characters`;
        if (field?.errors?.['maxlength']) return `Maximum ${field.errors['maxlength'].requiredLength} characters`;
        return '';
    }

    private markFormGroupTouched(formGroup: FormGroup): void {
        Object.values(formGroup.controls).forEach(control => {
            control.markAsTouched();
        });
    }
}
